// #region imports

// --- Angular
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// --- RxJs
import { BehaviorSubject, fromEvent, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// --- HS Code
import { AddJournalEntryForm } from '../../models';

// #endregion

@Component({
  selector: 'hs-add-journal-entry',
  templateUrl: './add-journal-entry.component.html',
  styleUrls: ['./add-journal-entry.component.scss'],
})
export class AddJournalEntryComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() entryText: string = '';

  @Input() containerId: string = '';
  @Input() scrollOffsetY = 0;
  @Input() scrollOffsetX = 0;
  @Input() static: boolean = false;

  @Output() entrySubmit = new EventEmitter<AddJournalEntryForm>();

  entryForm: FormGroup;
  scrolled$: BehaviorSubject<boolean>;

  get scrollContainer(): HTMLElement | Window {
    const container = document.getElementById(this.containerId);
    return container || window;
  }

  private _scrollEventSubscription: Subscription = Subscription.EMPTY;

  constructor(private fb: FormBuilder, private elementRef: ElementRef) {
    this.scrolled$ = new BehaviorSubject<boolean>(false);
    this.entryForm = this.fb.group({
      entryText: ['', Validators.required],
    });
  }

  ngAfterViewInit(): void {
    this._scrollEventSubscription = fromEvent(this.scrollContainer, 'scroll')
      .pipe(debounceTime(100))
      .subscribe(this._onContainerScroll.bind(this));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes['entryText'] && changes['entryText'].currentValue) {
      this._updateForm(changes['entryText'].currentValue);
    }
  }

  ngOnDestroy(): void {
    if (this._scrollEventSubscription) {
      this._scrollEventSubscription.unsubscribe();
      this._scrollEventSubscription = Subscription.EMPTY;
    }
  }

  onEntrySubmit() {
    if (this.entryForm.invalid) {
      return;
    }
    this.entrySubmit.emit(this.entryForm.value);
    this.entryForm.reset();
  }

  private _updateForm(entryText: string) {
    this.entryForm.get('entryText')?.setValue(entryText);
  }

  private _onContainerScroll(event: Event) {
    if (this.scrollContainer instanceof Window) {
      if (window.pageYOffset > this.scrollOffsetY) {
        this.scrolled$.next(true);
      } else {
        this.scrolled$.next(false);
      }
      return;
    }
    const elementRef = this.elementRef.nativeElement as HTMLElement;
    const elementDimensions = elementRef.getBoundingClientRect() as DOMRect;
    const container = this.scrollContainer;
    if (
      elementDimensions.x + elementDimensions.width < this.scrollOffsetX ||
      elementDimensions.y + elementDimensions.height < this.scrollOffsetY ||
      elementDimensions.x > container.offsetWidth ||
      elementDimensions.y > container.offsetHeight
    ) {
      this.scrolled$.next(true);
    } else {
      this.scrolled$.next(false);
    }
  }
}
